import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RouterStateTrackerService } from 'src/app/services/router-state-tracker.service';

type SlidePosition = '/calendar' | '/menu' | '/order' | '/notifications' | '/accounts' | string;

@Component({
  selector: 'app-slide-bar',
  templateUrl: './slide-bar.component.html',
  styleUrls: ['./slide-bar.component.scss'],
  standalone: false
})
export class SlideBarComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container') container!: ElementRef<HTMLDivElement>;
  @ViewChild('handle') handle!: ElementRef<HTMLDivElement>;

  @Output() positionChange = new EventEmitter<{ current: SlidePosition; previous: SlidePosition }>();

  @Input() currentActiveRoute?: string;
  @Input() showOrders: boolean = true;

  private readonly ROUTE_POSITIONS: Record<string, number> = {
    '/calendar': 0,
    '/menu': 24,
    '/order': 48
  };
  private readonly OFF_ROUTES: string[] = ['/accounts', '/notifications'];
  private readonly SNAP_POSITIONS: number[] = [0, 0.5, 1];

  tabs = [
    { route: '/calendar', iconSvgPath: '/assets/svg/calendar.svg', id: 'nav-1' },
    { route: '/menu', iconSvgPath: '/assets/svg/menu-n.svg', id: 'nav-2' },
    { route: '/order', iconSvgPath: '/assets/svg/carts.svg', id: 'nav-3' },
    { route: '/accounts', iconSvgPath: '/assets/svg/user.svg', id: 'nav-4' },
  ];

  private containerWidth = 0;
  private handleWidth = 0;
  private dragging = false;
  private dragStartLocked = false;
  private lastX = 0;
  private handleLeft = 0;
  private targetLeft = 0;
  private velocity = 0;
  private animationFrame?: number;
  private dragRAF: number | null = null;
  private pendingDragDelta = 0;
  private subscriptions = new Subscription();
  private eventListeners: Array<{element: any, event: string, handler: any, options?: any}> = [];
  private navigationDebounceTimer?: any;
  private lastEmittedRoute: string = '';
  private isUserInitiated = false;
  private userInitiatedTimeout?: any;

  public currentRoute: SlidePosition = '';
  private previousRoute: SlidePosition = '';
  canSlide: boolean = true;

  readonly SPRING_STRENGTH = 0.035;
  readonly DAMPING_FACTOR = 0.88;
  readonly REST_VELOCITY_THRESHOLD = 0.01;
constructor(private routerStateTracker: RouterStateTrackerService) {
    this.subscriptions.add(
      this.routerStateTracker.isNavigating$.subscribe(val => {
        const wasSliding = this.canSlide;
        this.canSlide = !val;
        if (wasSliding && !this.canSlide && this.dragging) {
          this.cleanupDragState();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentActiveRoute'] && !changes['currentActiveRoute'].firstChange) {
      if (this.dragging || this.isUserInitiated) {
        return;
      }
      const newRoute = changes['currentActiveRoute'].currentValue;
      if (this.currentRoute === newRoute) {
        return;
      }
      this.updateFromExternalNavigation(newRoute);
    }
  }

  ngAfterViewInit() {
    this.recalculateDimensions();
    this.currentRoute = this.currentActiveRoute || '';
    this.previousRoute = this.currentRoute;
    this.lastEmittedRoute = this.currentRoute;
    this.initializePosition(this.currentRoute);

    this.addEventListenerTracked(this.handle?.nativeElement, 'mousedown', this.startDrag.bind(this));
    this.addEventListenerTracked(this.handle?.nativeElement, 'touchstart', this.startDrag.bind(this), { passive: false });
    this.addEventListenerTracked(document, 'mousemove', this.onDrag.bind(this));
    this.addEventListenerTracked(document, 'touchmove', this.onDrag.bind(this), { passive: false });
    this.addEventListenerTracked(document, 'mouseup', this.stopDrag.bind(this));
    this.addEventListenerTracked(document, 'touchend', this.stopDrag.bind(this));
    this.addEventListenerTracked(this.container?.nativeElement, 'click', this.jumpToPosition.bind(this));

    this.animate();
  }

  private recalculateDimensions(): boolean {
    const container = this.container?.nativeElement;
    const handle = this.handle?.nativeElement;
    if (!container || !handle) return false;
    const newContainerWidth = container.offsetWidth;
    const newHandleWidth = handle.offsetWidth;
    if (newContainerWidth <= 0 || newHandleWidth <= 0) return false;
    this.containerWidth = newContainerWidth;
    this.handleWidth = newHandleWidth;
    return true;
  }

  private initializePosition(route: string): void {
    if (!this.handle?.nativeElement) return;
    const handleEl = this.handle.nativeElement;
    if (this.isOffRoute(route)) {
      handleEl.style.opacity = '0';
      handleEl.style.pointerEvents = 'none';
      return;
    }
    handleEl.style.opacity = '1';
    handleEl.style.pointerEvents = 'auto';
    const pixelPos = this.routeToPixelPosition(route);
    if (pixelPos !== null) {
      this.handleLeft = pixelPos;
      this.targetLeft = pixelPos;
      handleEl.style.left = `${pixelPos}px`;
    }
  }

  private updateFromExternalNavigation(route: string): void {
    if (!this.recalculateDimensions()) {
      setTimeout(() => this.updateFromExternalNavigation(route), 50);
      return;
    }
    this.previousRoute = this.currentRoute;
    this.currentRoute = route;
    this.lastEmittedRoute = route;
    this.updateHandleForRoute(route);
  }

  private setUserInitiated(): void {
    this.isUserInitiated = true;
    if (this.userInitiatedTimeout) {
      clearTimeout(this.userInitiatedTimeout);
    }
    this.userInitiatedTimeout = setTimeout(() => {
      this.isUserInitiated = false;
    }, 150);
  }

  private addEventListenerTracked(element: any, event: string, handler: any, options?: any) {
    if (!element) return;
    element.addEventListener(event, handler, options);
    this.eventListeners.push({ element, event, handler, options });
  }

  private isOffRoute(route: string): boolean {
    return this.OFF_ROUTES.includes(route) || !(route in this.ROUTE_POSITIONS);
  }

  private routeToPixelPosition(route: string): number | null {
    const vwValue = this.ROUTE_POSITIONS[route];
    if (vwValue === undefined) return null;
    return (vwValue / 100) * window.innerWidth;
  }

  private routeToIndex(route: string): number {
    const routes = Object.keys(this.ROUTE_POSITIONS);
    return routes.indexOf(route);
  }

  private indexToRoute(index: number): SlidePosition {
    const routes = Object.keys(this.ROUTE_POSITIONS);
    return routes[index] || '/calendar';
  }

  canClick(route: string): string {
    return this.currentRoute === route ? 'noClick' : 'canClick';
  }

  iconColors(route: string): string {
    if (this.isOffRoute(this.currentRoute) && !this.isOffRoute(route)) {
      return 'unSelectedIcon';
    }
    return route === this.currentRoute ? 'selectedIcon' : 'unSelectedIcon';
  }

  jiggleClass(): string {
    switch (this.currentRoute) {
      case '/calendar': return 'calendarCut';
      case '/menu': return 'menuCut';
      case '/order': return 'orderCut';
      default: return '';
    }
  }

  private updateHandleForRoute(route: string): void {
    if (!this.handle?.nativeElement) return;
    const handleEl = this.handle.nativeElement;
    if (this.isOffRoute(route)) {
      handleEl.style.opacity = '0';
      handleEl.style.pointerEvents = 'none';
      return;
    }
    handleEl.style.opacity = '1';
    handleEl.style.pointerEvents = 'auto';
    const pixelPos = this.routeToPixelPosition(route);
    if (pixelPos !== null) {
      this.targetLeft = pixelPos;
      this.startAnimation();
    }
  }

  private animate(): void {
    const handleEl = this.handle?.nativeElement;
    if (!handleEl) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
      return;
    }
    const jiggler = handleEl.querySelector('.jiggler') as HTMLElement;
    if (!jiggler) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
      return;
    }
    if (this.containerWidth <= 0 || this.handleWidth <= 0) {
      this.recalculateDimensions();
      this.animationFrame = requestAnimationFrame(() => this.animate());
      return;
    }
    const maxLeft = this.containerWidth - this.handleWidth;
    if (this.isOffRoute(this.currentRoute)) {
      handleEl.style.opacity = '0';
      handleEl.style.pointerEvents = 'none';
      this.animationFrame = requestAnimationFrame(() => this.animate());
      return;
    }
    handleEl.style.opacity = '1';
    handleEl.style.pointerEvents = 'auto';
    const diff = this.targetLeft - this.handleLeft;
    this.velocity = this.velocity * this.DAMPING_FACTOR + diff * this.SPRING_STRENGTH;
    this.handleLeft += this.velocity;
    this.handleLeft = Math.max(0, Math.min(this.handleLeft, maxLeft));
    const isAtRest = Math.abs(this.velocity) < this.REST_VELOCITY_THRESHOLD && Math.abs(diff) < 0.5;
    if (isAtRest && !this.dragging) {
      this.handleLeft = this.targetLeft;
      handleEl.style.left = `${this.handleLeft}px`;
      jiggler.style.transform = 'scaleX(1) scaleY(1)';
      this.animationFrame = undefined;
      return;
    }
    handleEl.style.left = `${this.handleLeft}px`;
    if (Math.abs(this.velocity) > 0.1) {
      const movementIntensity = Math.min(Math.abs(this.velocity) * 0.3, 0.15);
      const scaleX = 1 + movementIntensity * 0.5;
      const scaleY = 1 - movementIntensity * 0.3;
      jiggler.style.transform = `scaleX(${scaleX}) scaleY(${scaleY})`;
    } else {
      jiggler.style.transform = 'scaleX(1) scaleY(1)';
    }
    this.animationFrame = requestAnimationFrame(() => this.animate());
  }

  private startAnimation(): void {
    if (!this.animationFrame) {
      this.animate();
    }
  }

  startDrag(event: MouseEvent | TouchEvent): void {
    if (!this.canSlide || this.isOffRoute(this.currentRoute)) return;
    event.preventDefault();
    this.dragging = true;
    this.dragStartLocked = this.canSlide;
    this.lastX = this.getClientX(event);
    this.setUserInitiated();
    this.startAnimation();
  }

  onDrag(event: MouseEvent | TouchEvent): void {
    if (!this.dragging || !this.dragStartLocked) return;
    event.preventDefault();
    const clientX = this.getClientX(event);
    const deltaX = clientX - this.lastX;
    this.lastX = clientX;
    this.pendingDragDelta += deltaX;
    
    if (this.dragRAF === null) {
      this.dragRAF = requestAnimationFrame(() => {
        const maxLeft = this.containerWidth - this.handleWidth;
        this.targetLeft = Math.max(0, Math.min(this.targetLeft + this.pendingDragDelta, maxLeft));
        this.pendingDragDelta = 0;
        this.dragRAF = null;
      });
    }
  }

  stopDrag(): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.dragStartLocked = false;
    this.snapToNearest();
  }

  private cleanupDragState(): void {
    this.dragging = false;
    this.dragStartLocked = false;
  }

  jumpToPosition(event: MouseEvent): void {
    if (!this.canSlide) return;
    if ((event.target as HTMLElement).closest('.slider-handle')) return;
    this.setUserInitiated();
    const rect = this.container?.nativeElement.getBoundingClientRect();
    if (!rect) return;
    const clickX = event.clientX - rect.left;
    const maxLeft = this.containerWidth - this.handleWidth;
    this.targetLeft = Math.max(0, Math.min(clickX - this.handleWidth / 2, maxLeft));
    this.startAnimation();
    this.snapToNearest();
  }

  private snapToNearest(): void {
    const maxLeft = this.containerWidth - this.handleWidth;
    if (maxLeft <= 0) return;
    const normalized = this.targetLeft / maxLeft;
    let closestIndex = 0;
    let minDist = Math.abs(this.SNAP_POSITIONS[0] - normalized);
    for (let i = 1; i < this.SNAP_POSITIONS.length; i++) {
      const d = Math.abs(this.SNAP_POSITIONS[i] - normalized);
      if (d < minDist) {
        minDist = d;
        closestIndex = i;
      }
    }
    const snappedRoute = this.indexToRoute(closestIndex);
    const snapPosition = this.SNAP_POSITIONS[closestIndex];
    if (snapPosition !== undefined) {
      this.targetLeft = snapPosition * maxLeft;
    }
    if (this.currentRoute !== snappedRoute) {
      const oldRoute = this.currentRoute;
      this.currentRoute = snappedRoute;
      if (this.navigationDebounceTimer) {
        clearTimeout(this.navigationDebounceTimer);
      }
      this.navigationDebounceTimer = setTimeout(() => {
        if (this.lastEmittedRoute !== snappedRoute) {
          this.lastEmittedRoute = snappedRoute;
          this.positionChange.emit({
            current: snappedRoute,
            previous: oldRoute
          });
          this.previousRoute = snappedRoute;
        }
      }, 50);
    }
  }

  setPosition(pos: SlidePosition): void {
    if (!this.canSlide) return;
    this.setUserInitiated();
    if (this.isOffRoute(pos)) {
      if (this.handle?.nativeElement) {
        this.handle.nativeElement.style.opacity = '0';
        this.handle.nativeElement.style.pointerEvents = 'none';
      }
      const oldRoute = this.currentRoute;
      this.currentRoute = pos;
      this.positionChange.emit({ current: pos, previous: oldRoute });
      this.previousRoute = pos;
      this.lastEmittedRoute = pos;
      return;
    }
    if (this.isOffRoute(this.currentRoute) && this.handle?.nativeElement) {
      this.handle.nativeElement.style.opacity = '1';
      this.handle.nativeElement.style.pointerEvents = 'auto';
    }
    if (!this.recalculateDimensions()) {
      setTimeout(() => this.setPosition(pos), 50);
      return;
    }
    const pixelPos = this.routeToPixelPosition(pos);
    if (pixelPos !== null) {
      this.targetLeft = pixelPos;
      this.startAnimation();
      this.snapToNearest();
    }
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    return event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX ?? 0;
  }

  ngOnDestroy(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    if (this.dragRAF !== null) {
      cancelAnimationFrame(this.dragRAF);
      this.dragRAF = null;
    }
    this.eventListeners.forEach(({ element, event, handler }) => {
      if (element) {
        element.removeEventListener(event, handler);
      }
    });
    this.eventListeners = [];
    this.subscriptions.unsubscribe();
    if (this.navigationDebounceTimer) {
      clearTimeout(this.navigationDebounceTimer);
    }
    if (this.userInitiatedTimeout) {
      clearTimeout(this.userInitiatedTimeout);
    }
  }
}
